// deno-lint-ignore-file no-window
import { Injectable } from '@angular/core';
import { vimeoRateSchema, vimeoSeekToSchema, vimeoVolumeSchema } from '@shared/ragai.schemas';
import { z } from 'zod';
import { EventManagerService } from './event-manager/event-manager.service';
import { HelperManagerService } from './helper-manager.service';

type VimeoObject = {
  getCurrentTime: () => Promise<number>;
  setCurrentTime: (seconds: number) => Promise<void>;
  play: () => Promise<void>;
  pause: () => Promise<void>;
  setVolume: (volume: number) => Promise<number>;
  setPlaybackRate: (playbackRate: number) => Promise<void>;
  getColors: () => Promise<[]>;
  setColors: (colors: []) => Promise<[]>;
  loadVideo: (id?: number, url?: string, object?: object) => Promise<number | string | object>;
  setLoop: (value: boolean) => Promise<boolean>;
};
/**
 * Steuert eingebettete Vimeo-Videos (`RAGAI.VIMEO`).
 * @remarks
 * Wie bei `RAGAI.YOUTUBE` wird der Player-Handle (`this.vimeo`) erst durch das Event `RAGAI.VIMEO.playerready`
 * gesetzt - Methodenaufrufe vor diesem Zeitpunkt (kein Vimeo-Player im Bot vorhanden bzw. noch nicht bereit)
 * schlagen fehl.
 */
@Injectable({
  providedIn: 'root'
})

export class VimeoManagerService {
  public vimeo!: VimeoObject;
  constructor(
    private eventManager: EventManagerService,
    private ragAiHelper: HelperManagerService
  ) {
    this.eventManager.on('RAGAI.VIMEO.playerready', (_event, vimeoObj) => {
      this.vimeo = vimeoObj as VimeoObject;
    });
  }

  /** Startet die Wiedergabe des Vimeo-Videos. */
  public async play() {
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.VIMEO.PLAY', {});
    await this.vimeo.play();
  }

  /** Pausiert die Wiedergabe des Vimeo-Videos. */
  public async pause() {
    (window.parent as Window & { vimeoispaused: boolean }).vimeoispaused = true;
    setTimeout(() => {
      (window.parent as Window & { vimeoispaused: boolean }).vimeoispaused = false;
    }, 1000);
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.VIMEO.PAUSE', {});
    await this.vimeo.pause();
  }

  /**
   * Gibt die aktuelle Abspielposition des Vimeo-Videos zurück.
   * @returns Die aktuelle Position in Sekunden.
   */
  public async currentPlayPosition(): Promise<number> {
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.VIMEO.CURRENTPLAYPOSITION', {});
    return await this.vimeo.getCurrentTime();
  }

  /**
   * Springt zu einer bestimmten Position im Vimeo-Video.
   * @param hours Stunden, oder ein Objekt gemäß `vimeoSeekToSchema`.
   * @param minutes Minuten.
   * @param seconds Sekunden.
   */
  public async seekTo(hours: number | z.input<typeof vimeoSeekToSchema>, minutes: number = 0, seconds: number = 0) {
    const parsed = vimeoSeekToSchema.safeParse(hours);
    if (parsed.success && typeof hours === 'object') {
      minutes = parsed.data.minutes;
      seconds = parsed.data.seconds;
      hours = parsed.data.hours;
    }
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.VIMEO.SEEKTO', { seconds });
    seconds = this.ragAiHelper.timeToSeconds(hours as number, minutes, seconds);
    await this.vimeo.setCurrentTime(seconds);
  }
  /**
   * Setzt die Lautstärke des Vimeo-Videos.
   * @param percentageVolume Lautstärke in Prozent (0-100), oder ein Objekt gemäß `vimeoVolumeSchema`.
   * @returns Die tatsächlich gesetzte Lautstärke.
   */
  public async setVolume(percentageVolume: number | z.input<typeof vimeoVolumeSchema>) {
    const parsed = vimeoVolumeSchema.safeParse(percentageVolume);
    if (parsed.success && typeof percentageVolume === 'object') {
      percentageVolume = parsed.data.volume;
    }
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.VIMEO.SETVOLUME', { percentageVolume });
    const volume = (percentageVolume as number) / 100;
    return await this.vimeo.setVolume(volume);
  }

  /**
   * Aktiviert oder deaktiviert die Endlosschleife des Vimeo-Videos.
   * @param value `true` aktiviert die Endlosschleife.
   * @returns Der gesetzte Loop-Status.
   */
  public async setLoop(value: boolean) {
    return await this.vimeo.setLoop(value);
  }

  /**
   * Setzt die Wiedergabegeschwindigkeit des Vimeo-Videos.
   * @param playbackRate Wiedergabegeschwindigkeit (1 = normal), oder ein Objekt gemäß `vimeoRateSchema`.
   */
  public async setPlaybackRate(playbackRate: number | z.input<typeof vimeoRateSchema>) {
    const parsed = vimeoRateSchema.safeParse(playbackRate);
    if (parsed.success && typeof playbackRate === 'object') {
      playbackRate = parsed.data.rate;
    }
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.VIMEO.SETPLAYBACKRATE', { playbackRate });
    await this.vimeo.setPlaybackRate(playbackRate as number);
  }
  /**
   * Gibt die aktuellen Farbeinstellungen des Vimeo-Players zurück.
   * @returns Die aktuellen Farben.
   */
  public async getColors() {
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.VIMEO.GETCOLORS', {});
    return await this.vimeo.getColors();
  }
  /**
   * Setzt die Farbeinstellungen des Vimeo-Players.
   * @param colors Die zu setzenden Farben.
   * @returns Die gesetzten Farben.
   */
  public async setColors(colors: []) {
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.VIMEO.SETCOLORS', { colors });
    return await this.vimeo.setColors(colors);
  }
  /**
   * Lädt ein anderes Vimeo-Video in den Player.
   * @param id Numerische Vimeo-Video-ID.
   * @param url URL des Vimeo-Videos (Alternative zu `id`).
   * @param object Weitere Player-Optionen.
   * @returns Das Ergebnis des Ladevorgangs.
   */
  public async loadVideo(id?: number, url?: string, object?: object) {
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.VIMEO.LOADVIDEO', { id, url, object });
    return await this.vimeo.loadVideo(id, url, object);
  }
}